import React, { useState, useEffect } from 'react';
import { Zap, Fuel, TrendingUp, Link2 } from 'lucide-react';
import EnhancedTokenFeed from './EnhancedTokenFeed';
import { useRecentSignals } from '../../hooks/useDuckSignals';
import { useOnChainStats } from '../../hooks/useOnChainStats';
import { usePublicClient } from 'wagmi';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    blue: '#3b82f6',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '1.5rem',
    },
    section: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        overflow: 'hidden',
    },
    header: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    title: {
        fontSize: '0.875rem',
        fontWeight: 700,
        color: '#fff',
        margin: 0,
        letterSpacing: '0.02em',
        textTransform: 'uppercase' as const,
    },
    liveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.375rem 0.875rem',
        background: `${THEME.green}20`,
        border: `1px solid ${THEME.green}40`,
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: THEME.green,
    },
    pulse: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: THEME.green,
        animation: 'pulse 2s infinite',
    },
    body: {
        padding: '1.5rem',
        maxHeight: '400px',
        overflowY: 'auto' as const,
    },
    feedItem: {
        padding: '1rem',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '12px',
        marginBottom: '0.75rem',
        transition: 'all 0.2s',
    },
    feedHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.5rem',
    },
    feedTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#fff',
    },
    feedTime: {
        fontSize: '0.7rem',
        color: '#9ca3af',
    },
    feedDetails: {
        fontSize: '0.8rem',
        color: '#d1d5db',
        lineHeight: 1.5,
    },
    badge: (color: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.75rem',
        background: `${color}20`,
        border: `1px solid ${color}40`,
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: 600,
        color,
    }),
    emptyState: {
        textAlign: 'center' as const,
        padding: '3rem 1rem',
        color: '#9ca3af',
    },
};

function timeAgo(timestamp: number): string {
    const diff = (Date.now() - timestamp) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT SIGNALS FEED (reads from on-chain DuckSignals)
// ═══════════════════════════════════════════════════════════════════════════════

const AgentSignalsFeed: React.FC = () => {
    const { signals } = useRecentSignals(20);

    const getSignalColor = (type: string) => {
        if (type === 'BUY') return THEME.green;
        if (type === 'SELL') return THEME.red;
        return THEME.yellow;
    };

    return (
        <div style={styles.section}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Zap size={18} color={THEME.purple} />
                    <h3 style={styles.title}>On-Chain Agent Signals</h3>
                </div>
                <div style={styles.liveBadge}>
                    <span style={styles.pulse} />
                    LIVE
                </div>
            </div>
            <div style={styles.body}>
                {signals && signals.length > 0 ? (
                    signals.map((signal, i) => (
                        <div key={i} style={styles.feedItem}>
                            <div style={styles.feedHeader}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={styles.feedTitle}>{signal.signalType}</span>
                                    <span style={styles.badge(getSignalColor(signal.signalType))}>
                                        {Number(signal.confidence)}% confidence
                                    </span>
                                </div>
                                <span style={styles.feedTime}>{timeAgo(Number(signal.timestamp) * 1000)}</span>
                            </div>
                            <div style={styles.feedDetails}>
                                <div>{signal.reason || 'Agent analysis'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    Agent: {signal.agent?.slice(0, 8)}...{signal.agent?.slice(-4)}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={styles.emptyState}>
                        <Zap size={48} color={THEME.purple} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                        <p>No on-chain signals yet</p>
                        <p style={{ fontSize: '0.75rem' }}>Agents post signals every 15 minutes</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GAS TRACKER (real data from RPC)
// ═══════════════════════════════════════════════════════════════════════════════

interface GasEntry {
    id: string;
    gasGwei: number;
    recommendation: 'TRADE_NOW' | 'WAIT' | 'CAUTION';
    timestamp: number;
}

const GasFeed: React.FC = () => {
    const [gasUpdates, setGasUpdates] = useState<GasEntry[]>([]);
    const publicClient = usePublicClient();

    useEffect(() => {
        const fetchGas = async () => {
            if (!publicClient) return;
            try {
                const gasPrice = await publicClient.getGasPrice();
                const gwei = Number(gasPrice) / 1e9;

                let recommendation: GasEntry['recommendation'];
                if (gwei < 30) recommendation = 'TRADE_NOW';
                else if (gwei < 70) recommendation = 'CAUTION';
                else recommendation = 'WAIT';

                setGasUpdates(prev => [{
                    id: Date.now().toString(),
                    gasGwei: gwei,
                    recommendation,
                    timestamp: Date.now(),
                }, ...prev].slice(0, 15));
            } catch {
                // Silently handle RPC errors
            }
        };

        fetchGas();
        const interval = setInterval(fetchGas, 15000);
        return () => clearInterval(interval);
    }, [publicClient]);

    const getRecColor = (rec: GasEntry['recommendation']) => {
        if (rec === 'TRADE_NOW') return THEME.green;
        if (rec === 'CAUTION') return THEME.orange;
        return THEME.red;
    };

    return (
        <div style={styles.section}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Fuel size={18} color={THEME.orange} />
                    <h3 style={styles.title}>Gas Price Monitor</h3>
                </div>
                <div style={styles.liveBadge}>
                    <span style={styles.pulse} />
                    LIVE RPC
                </div>
            </div>
            <div style={styles.body}>
                {gasUpdates.length > 0 ? (
                    gasUpdates.map(update => (
                        <div key={update.id} style={styles.feedItem}>
                            <div style={styles.feedHeader}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={styles.feedTitle}>{update.gasGwei.toFixed(2)} gwei</span>
                                    <span style={styles.badge(getRecColor(update.recommendation))}>
                                        {update.recommendation === 'TRADE_NOW' ? 'OPTIMAL' : update.recommendation}
                                    </span>
                                </div>
                                <span style={styles.feedTime}>{timeAgo(update.timestamp)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={styles.emptyState}>
                        <Fuel size={48} color={THEME.orange} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                        <p>Connecting to Monad RPC...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MARKET STATS FEED (real DexScreener data)
// ═══════════════════════════════════════════════════════════════════════════════

const MarketStatsFeed: React.FC<{ tokenAddress?: string }> = ({ tokenAddress: addr }) => {
    const stats = useOnChainStats(addr, 900_000);

    return (
        <div style={styles.section}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Link2 size={18} color={THEME.blue} />
                    <h3 style={styles.title}>Market Overview</h3>
                </div>
                <div style={styles.liveBadge}>
                    <span style={styles.pulse} />
                    LIVE
                </div>
            </div>
            <div style={styles.body}>
                {stats ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {[
                            { label: 'Holders', value: stats.holders?.toLocaleString() || 'N/A', color: THEME.purple },
                            { label: 'Volume 24h', value: stats.volume24h ? `$${stats.volume24h.toLocaleString()}` : 'N/A', color: THEME.green },
                            { label: 'Liquidity', value: stats.liquidity ? `$${stats.liquidity.toLocaleString()}` : 'N/A', color: THEME.blue },
                            { label: 'Market Cap', value: stats.marketCap ? `$${stats.marketCap.toLocaleString()}` : 'N/A', color: THEME.yellow },
                            { label: '24h Buys', value: stats.buys24h?.toString() || '0', color: THEME.green },
                            { label: '24h Sells', value: stats.sells24h?.toString() || '0', color: THEME.red },
                            { label: 'Buy/Sell Ratio', value: stats.sells24h ? (stats.buys24h / stats.sells24h).toFixed(2) : 'N/A', color: (stats.buys24h || 0) >= (stats.sells24h || 0) ? THEME.green : THEME.red },
                            { label: 'Price USD', value: stats.priceUsd ? `$${stats.priceUsd}` : 'N/A', color: THEME.yellow },
                        ].map((item, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
                            }}>
                                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{item.label}</span>
                                <span style={{ color: item.color, fontWeight: 700, fontSize: '0.875rem' }}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={styles.emptyState}>
                        <Link2 size={48} color={THEME.blue} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                        <p>Loading market data...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const AdvancedAgentsFeed: React.FC<{ tokenAddress?: string; agentSocket?: any }> = ({ tokenAddress }) => {
    const [activeTab, setActiveTab] = useState<'signals' | 'agent-signals' | 'market' | 'gas'>('signals');

    const tabStyle = (isActive: boolean) => ({
        padding: '0.75rem 1.5rem',
        background: isActive ? `linear-gradient(135deg, #836EF9, #5e17eb)` : 'transparent',
        border: `1px solid ${isActive ? '#836EF9' : 'rgba(255, 215, 0, 0.2)'}`,
        borderRadius: '12px',
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: isActive ? '0 0 20px rgba(131, 110, 249, 0.3)' : 'none',
    } as React.CSSProperties);

    return (
        <div style={styles.container}>
            <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '2rem',
                padding: '0.75rem',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                flexWrap: 'wrap',
            }}>
                <button onClick={() => setActiveTab('signals')} style={tabStyle(activeTab === 'signals')}>
                    <TrendingUp size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                    Trading Signals
                </button>
                <button onClick={() => setActiveTab('agent-signals')} style={tabStyle(activeTab === 'agent-signals')}>
                    <Zap size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                    Agent Signals
                </button>
                <button onClick={() => setActiveTab('market')} style={tabStyle(activeTab === 'market')}>
                    <Link2 size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                    Market Data
                </button>
                <button onClick={() => setActiveTab('gas')} style={tabStyle(activeTab === 'gas')}>
                    <Fuel size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                    Gas Tracker
                </button>
            </div>

            {activeTab === 'signals' && <EnhancedTokenFeed tokenAddress={tokenAddress} />}
            {activeTab === 'agent-signals' && <AgentSignalsFeed />}
            {activeTab === 'market' && <MarketStatsFeed tokenAddress={tokenAddress} />}
            {activeTab === 'gas' && <GasFeed />}
        </div>
    );
};

export default AdvancedAgentsFeed;
